import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { EventInstance, EventInstanceSite, EventTemplateSite } from '../../../types';
import { SiteDisplayInfo } from '../../../types/site';
import { siteService, getStoreSiteTypes, getStoreSiteSections } from '../../../services/siteService';
import { authService } from '../../../services/auth';
import { formatCurrency } from '../../../utils/formatters';
import { formatDate } from '../../../utils/dateUtils';

interface EventSiteSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (siteIds: number[]) => void;
  event: Partial<EventInstance>;
  assignedSites: (EventInstanceSite | EventTemplateSite)[];
  loading?: boolean;
}

const formatDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';
  // Event dates are calendar days (YYYY-MM-DD); formatDate keeps them local.
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

const EventSiteSelector: React.FC<EventSiteSelectorProps> = ({
  isOpen,
  onClose,
  onSave,
  event,
  assignedSites,
  loading: externalLoading = false
}) => {
  const [sites, setSites] = useState<SiteDisplayInfo[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(
    new Set(assignedSites.map(s => s.site_id))
  );
  const [saving, setSaving] = useState(false);

  const storeId = authService.getStoreId();

  // Load sites from API - optimized to use 3 calls instead of N×4
  const loadSites = useCallback(async () => {
    try {
      setLoadingSites(true);

      // Fetch lookup data and sites in parallel (3 calls total)
      const [types, sections, sitesData] = await Promise.all([
        getStoreSiteTypes(storeId || undefined),
        getStoreSiteSections(storeId || undefined),
        siteService.getSites(storeId || undefined)
      ]);

      // Create lookup maps for efficient name resolution
      const typeMap = new Map(types.map(t => [t.id, t.name || '']));
      const sectionMap = new Map(sections.map(s => [s.id, s.name]));

      // Transform sites with mapped names
      const displaySites: SiteDisplayInfo[] = sitesData.map(site => ({
        ...site,
        type_name: site.type_id ? typeMap.get(site.type_id) : undefined,
        section_name: site.site_section_id ? sectionMap.get(site.site_section_id) : undefined,
        status: site.seasonal ? 'seasonal' as const : 'available' as const,
      }));

      setSites(displaySites);
    } catch (err) {
      console.error('Error loading sites:', err);
    } finally {
      setLoadingSites(false);
    }
  }, [storeId]);

  // Reset selection and load sites when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSiteIds(new Set(assignedSites.map(s => s.site_id)));
      setSearchTerm('');
      setSelectedSection('all');
      setSelectedType('all');
      loadSites();
    }
  }, [isOpen, assignedSites, loadSites]);

  // Get unique sections and site types
  const { sections, siteTypes } = useMemo(() => {
    const sectionSet = new Set<string>();
    const typeSet = new Set<string>();

    sites.forEach(site => {
      if (site.section_name) {
        sectionSet.add(site.section_name);
      }
      if (site.type_name) {
        typeSet.add(site.type_name);
      }
    });

    return {
      sections: Array.from(sectionSet).sort(),
      siteTypes: Array.from(typeSet).sort()
    };
  }, [sites]);

  // Filter sites
  const filteredSites = useMemo(() => {
    return sites.filter(site => {
      // Exclude seasonal sites
      if (site.seasonal || site.status === 'seasonal') {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          site.alias?.toLowerCase().includes(search) ||
          site.description?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Section filter
      if (selectedSection !== 'all' && site.section_name !== selectedSection) {
        return false;
      }

      // Type filter
      if (selectedType !== 'all' && site.type_name !== selectedType) {
        return false;
      }

      return true;
    });
  }, [sites, searchTerm, selectedSection, selectedType]);

  // Group sites by section for display
  const groupedSites = useMemo(() => {
    const groups: { [key: string]: SiteDisplayInfo[] } = {};

    filteredSites.forEach(site => {
      const section = site.section_name || 'Other';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(site);
    });

    // Sort sites within each group by alias
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => (a.alias || '').localeCompare(b.alias || '', undefined, { numeric: true }));
    });

    return groups;
  }, [filteredSites]);

  const toggleSite = (siteId: number) => {
    setSelectedSiteIds(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedSiteIds(prev => {
      const next = new Set(prev);
      filteredSites.forEach(site => {
        if (site.status !== 'blocked') {
          next.add(site.id);
        }
      });
      return next;
    });
  };

  const deselectAllVisible = () => {
    setSelectedSiteIds(prev => {
      const next = new Set(prev);
      filteredSites.forEach(site => {
        next.delete(site.id);
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      onSave(Array.from(selectedSiteIds));
      onClose();
    } catch (err) {
      console.error('Error saving sites:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isLoading = loadingSites || externalLoading;
  const selectedCount = selectedSiteIds.size;
  const availableCount = filteredSites.filter(s => s.status !== 'blocked').length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content site-selector-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="site-selector-header-content">
            <h2>Select Sites for Event</h2>
            <div className="site-selector-event-info">
              <span className="event-name">{event.name}</span>
              {event.start_date && event.end_date && (
                <span className="event-dates">{formatDateRange(event.start_date, event.end_date)}</span>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="site-selector-filters">
          <div className="filter-row">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sections</option>
              {sections.map(section => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              {siteTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="selection-actions">
            <button className="btn-link" onClick={selectAllVisible}>
              Select All ({availableCount})
            </button>
            <span className="separator">|</span>
            <button className="btn-link" onClick={deselectAllVisible}>
              Deselect All
            </button>
          </div>
        </div>

        <div className="site-selector-body">
          {isLoading ? (
            <div className="loading-message">Loading sites...</div>
          ) : Object.keys(groupedSites).length === 0 ? (
            <div className="no-sites-message">
              No sites match your filters
            </div>
          ) : (
            Object.entries(groupedSites).map(([sectionName, sectionSites]) => (
              <div key={sectionName} className="site-section">
                <h3 className="section-header">{sectionName}</h3>
                <div className="site-list">
                  {sectionSites.map(site => {
                    const isSelected = selectedSiteIds.has(site.id);
                    const isUnavailable = site.status === 'blocked';

                    return (
                      <div
                        key={site.id}
                        className={`site-item ${isSelected ? 'selected' : ''} ${isUnavailable ? 'unavailable' : ''}`}
                        onClick={() => !isUnavailable && toggleSite(site.id)}
                      >
                        <div className="site-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSite(site.id)}
                            disabled={isUnavailable}
                          />
                        </div>
                        <div className="site-info">
                          <div className="site-name-row">
                            <span className="site-alias">{site.alias}</span>
                            {site.section_name && (
                              <span className="site-section-badge">{site.section_name}</span>
                            )}
                            {site.type_name && (
                              <span className="site-type-badge">{site.type_name}</span>
                            )}
                          </div>
                          {site.description && (
                            <div className="site-description">{site.description}</div>
                          )}
                        </div>
                        <div className="site-price">
                          {site.current_price
                            ? `${formatCurrency(site.current_price)}/night`
                            : '-'}
                        </div>
                        <div className="site-status">
                          {isUnavailable ? (
                            <span className="status-unavailable">{site.status}</span>
                          ) : isSelected ? (
                            <span className="status-selected">Selected</span>
                          ) : (
                            <span className="status-available">Available</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="modal-footer site-selector-footer">
          <div className="selection-summary">
            <strong>{selectedCount}</strong> site{selectedCount !== 1 ? 's' : ''} selected
          </div>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn-save" onClick={handleSave} disabled={saving || isLoading}>
              {saving ? 'Saving...' : 'Save Site Selection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSiteSelector;
